import React, { Component } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { array, string, func } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { lazyLoadWithDimensions } from '../../util/contextHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_NIGHT,
  propTypes,
  getUserSubscriptionLevel,
  isClientType,
  isProviderType,
  isStartup,
  SUBSCRIPTION_LEVEL_1,
  SUBSCRIPTION_LEVEL_2,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { richText } from '../../util/richText';
import { findOptionsForSelectFilter } from '../../util/search';
import { createSlug } from '../../util/urlHelpers';
import config from '../../config';
import { NamedLink, ResponsiveImage, IconCheckmarkCircle, IconFire } from '../../components';
import { shortenString } from './utils';
import ListingAverageRating from '../ListingAverageRating/ListingAverageRating';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const getCertificateInfo = (certificateOptions, key) => {
  return certificateOptions.find(c => c.key === key);
};

class ListingImage extends Component {
  render() {
    return <ResponsiveImage {...this.props} />;
  }
}
const LazyImage = lazyLoadWithDimensions(ListingImage, { loadAfterInitialRendering: 3000 });

export const ListingCardComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    filtersConfig,
    setActiveListing,
    titleOnTop,
    isOffer,
    currentUser,
    isSearchPageCard,
    useAuthLink,
    history,
    onChangeGenericMessageOpen
  } = props;

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const secondImage =
    currentListing.images && currentListing.images.length > 1 ? currentListing.images[1] : null;
  const certificateOptions = findOptionsForSelectFilter('certificate', filtersConfig);
  const certificate = publicData
    ? getCertificateInfo(certificateOptions, publicData.certificate)
    : null;
  const { formattedPrice, priceTitle } = priceData(price, intl);

  const unitType = config.bookingUnitType;
  const isNightly = unitType === LINE_ITEM_NIGHT;
  const isDaily = unitType === LINE_ITEM_DAY;

  const unitTranslationKey = isNightly
    ? 'ListingCard.perNight'
    : isDaily
      ? 'ListingCard.perDay'
      : 'ListingCard.perUnit';
  const description = currentListing?.attributes?.description
    ? shortenString(currentListing?.attributes?.description, 35)
    : null;

  const shortDescription =
    currentListing?.attributes?.publicData?.shortDescription || 'Business tag line';

  const firstImageUrl = firstImage?.attributes?.variants?.['landscape-crop2x']?.url;
  const logoImageId = publicData?.logoPhotoIds && publicData?.logoPhotoIds[0];
  const authorImage = !!currentListing?.author?.profileImage?.id?.uuid && currentListing?.author?.profileImage;

  const coverImage = authorImage || firstImage;
  const customDisplayName = publicData?.customDisplayName;
  const isProviderVerified = publicData?.providerVerified;
  const isStartupOffer = publicData?.startupOfferTitle;

  const subscriptionLevel = publicData?.subscriptionLevel;

  const isClient = isClientType(currentUser);
  const isProvider = isProviderType(currentUser);
  const isStartupProvider = isStartup(currentUser);
  const userSubscriptionLevel = getUserSubscriptionLevel(currentUser);

  const listingType = publicData?.listingType;
  const providerType = publicData?.providerType;
  const isFreeListing = subscriptionLevel < SUBSCRIPTION_LEVEL_2;

  const isFreeStartup = currentUser && isStartupProvider && userSubscriptionLevel < SUBSCRIPTION_LEVEL_2;
  const isFreeEstablishedCompany = currentUser && isProvider && userSubscriptionLevel < SUBSCRIPTION_LEVEL_2;

  const startupCondition = isFreeStartup && isFreeListing;
  const establishedCompanyCondition = isFreeEstablishedCompany && isFreeListing;

  const disableListingLink = isClient
    ? false
    : isProvider
      ? (startupCondition || establishedCompanyCondition)
      : null;

  const classes = classNames(rootClassName || css.root, className, { [css.rootBlur]: disableListingLink });

  const onHandleLidtingLink = () => {
    // if (disableListingLink && onChangeGenericMessageOpen && !isClient) {
    //   onChangeGenericMessageOpen(true)
    //   setTimeout(() => {
    //     onChangeGenericMessageOpen(false)
    //   }, 2000)
    // } else {
    const routes = routeConfiguration();
    useAuthLink ? history.push(createResourceLocatorString('SignupPage', routes, {}, {})) :
      history.push(createResourceLocatorString('ListingPage', routes, { id, slug }, {}));
    // }
  }

  return (
    <div
      className={classes}
      // name={useAuthLink ? "SignupPage" : "ListingPage"} 
      // params={{ id, slug }}
      onClick={() => onHandleLidtingLink()}
    >
      {titleOnTop && (
        <div className={css.title} style={{ marginBottom: '20px' }}>
          {customDisplayName || richText(title, {
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}
        </div>
      )}
      <div
        className={css.threeToTwoWrapper}
        onMouseEnter={() => setActiveListing(currentListing.id)}
        onMouseLeave={() => setActiveListing(null)}
      >
        <div className={css.aspectWrapper}>
          <LazyImage
            rootClassName={css.rootForImage}
            alt={customDisplayName || title}
            image={isOffer ? firstImage : coverImage}
            variants={['landscape-crop-custom', 'square-small']}
            sizes={renderSizes}
          />
        </div>
      </div>
      {/* <img className={css.listingImage} src={firstImageUrl} /> */}
      {/* <h2>{subscriptionLevel}</h2> */}

      <div className={css.info}>
        <div className={css.mainInfo}>
          {!titleOnTop && (
            <div className={classNames(css.title, { [css.titleSearch]: isSearchPageCard })}>
              {customDisplayName || richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
          )}
          <div className={css.badgeHolder}>
            {isProviderVerified && (
              <div className={css.verifiedBadge}>
                <IconCheckmarkCircle />
                <FormattedMessage id="ListingCard.verified" />
              </div>
            )}
            {isStartupOffer && (
              <div className={css.offerBadge}>
                <IconFire />
                <FormattedMessage id="ListingCard.offer" />
              </div>
            )}
            {publicData?.isOffer ? <span className={css.price}>
              {formattedPrice}
            </span> : null}
          </div>
          <div className={classNames(css.description, { [css.descriptionOffer]: isOffer })}>{description && <span>{shortDescription}</span>}</div>
          {/* <Rating name="rating" value={4.5} precision={0.5} readOnly className={css.rating} /> */}
          {/* {!isOffer && ( */}
          <ListingAverageRating
            listing={listing}
            listingId={currentListing?.id?.uuid}
            currentUser={currentUser}
            className={css.averageRating}
          />
          {/* )} */}
        </div>
      </div>
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  filtersConfig: config.custom.filters,
  setActiveListing: () => null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  filtersConfig: array,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

const ListingCard = compose(
  withRouter,
  injectIntl
)(ListingCardComponent);

export default ListingCard;
